import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import {
  generateDepositLink,
  simulateStripeWebhookPaid,
  recordManualPayment,
} from '@/domain/depositFlow';
import {
  requiresNoDepositApproval,
  requestNoDepositOverride,
  waiveDepositDirectly,
} from '@/domain/approvals';
import { canApproveNoDepositOverride } from '@/domain/permissions';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import {
  priceForArtist,
  depositForSession,
  depositForDuration,
  sessionLengthForDuration,
  SESSION_DURATION_MIN,
} from '@/domain/pricing';
import { simTimeFull, simDateShort, customerName, aud, formatBodyPart } from '@/utils/format';
import { piecesForCustomer } from '@/utils/pieceHelpers';
import type { Booking, DayRateBasis, DesignType, Piece, SessionLength } from '@/domain/types';
import { inspirationFor, partitionReferences } from '@/utils/imageHelpers';
import ReferencePicker from '@/components/ReferencePicker';
import ArtistPicker from '@/components/booking/ArtistPicker';
import PlacementFields, { type PlacementValue } from '@/components/booking/PlacementFields';
import RateCardBlock from '@/components/booking/RateCardBlock';
import { X, Save, Send, Search, Check, ChevronDown, AlertTriangle, Banknote } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  open: boolean;
  studioId: string;
  artistId: string;
  startIso: string;
  onClose: () => void;
  defaultCustomerId?: string;
}

// Colour is the COLOUR toggle; style tags cover the remaining design dimensions
// (black-and-grey removed so it doesn't duplicate the colour choice).
const STYLE_TAG_OPTIONS = [
  'colour-realism',
  'traditional',
  'neo-traditional',
  'fine-line',
  'lettering',
  'blackwork',
  'sleeve',
  'flash',
  'cover-up',
];

type PieceMode = 'none' | 'continue' | 'new';

const PIECE_MODES: Array<{ key: PieceMode; label: string }> = [
  { key: 'continue', label: 'Continue existing piece' },
  { key: 'new', label: 'Start new piece' },
  { key: 'none', label: 'One-off / no piece' },
];

const pieceSessionNo = (p: Piece) => (p.sessionsCompleted ?? p.bookingsLinked.length) + 1;
const piecePlanRate = (p: Piece | undefined): number | undefined =>
  p?.pricingPlan?.find((x) => x.sessionIndex === pieceSessionNo(p))?.expectedDayRate;

export default function NewBookingModal({
  open,
  studioId,
  artistId,
  startIso,
  onClose,
  defaultCustomerId,
}: Props) {
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const pieces = useBrainStore((s) => s.pieces);
  const bookings = useBrainStore((s) => s.bookings);
  const upsertBooking = useBrainStore((s) => s.upsertBooking);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  const persona = useSessionStore((s) => s.persona);

  const heroes = Object.values(customers).filter((c) => c.hero);
  const [customerId, setCustomerId] = useState(defaultCustomerId ?? heroes[0]?.id ?? '');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerOpen, setCustomerOpen] = useState(false);
  const customerInputRef = useRef<HTMLDivElement>(null);

  const [pieceMode, setPieceMode] = useState<PieceMode>('none');
  const [newPieceSessions, setNewPieceSessions] = useState('');
  const [newPieceTitle, setNewPieceTitle] = useState('');
  const [continuePieceId, setContinuePieceId] = useState('');
  const [pieceDefaults, setPieceDefaults] = useState<{
    placementNote: string;
    colour: 'colour' | 'black-and-grey';
    styleTags: string[];
    artistId: string;
  } | null>(null);

  const [primaryArtistId, setPrimaryArtistId] = useState(artistId);
  const [collabArtistIds, setCollabArtistIds] = useState<string[]>([]);

  const [sessionLength, setSessionLength] = useState<SessionLength>('half-day');
  const [placement, setPlacement] = useState<PlacementValue>({ placementNote: '' });
  const [colour, setColour] = useState<'colour' | 'black-and-grey'>('black-and-grey');
  const [styleTags, setStyleTags] = useState<string[]>([]);
  const [selectedRefs, setSelectedRefs] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const [dayRateInput, setDayRateInput] = useState('');
  const [customHours, setCustomHours] = useState<number | null>(null);
  const [dayRateBasis, setDayRateBasis] = useState<DayRateBasis>('standard');
  const [depositInput, setDepositInput] = useState('');
  const [overrideActive, setOverrideActive] = useState(false);
  const [showRateReset, setShowRateReset] = useState(false);
  // Mandatory reason when booking with no deposit (below the studio minimum).
  const [overrideReason, setOverrideReason] = useState('');

  const [depositMenuOpen, setDepositMenuOpen] = useState(false);
  const depositMenuRef = useRef<HTMLDivElement>(null);

  const customer = customerId ? customers[customerId] : undefined;
  const activeArtist = artists[primaryArtistId];

  const studioArtists = useMemo(
    () =>
      Object.values(artists)
        .filter((a) => a.studioId === studioId && !a.isApprentice && !a.archived)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [artists, studioId],
  );
  // Resolves by customerId, not the customer.pieces back-link, which almost
  // nothing fills - reading that alone left Continue permanently disabled even
  // for clients with two unfinished pieces on file. See utils/pieceHelpers.
  const customerPieces = useMemo(
    () => (customer ? piecesForCustomer(pieces, customer) : []),
    [customer, pieces],
  );
  const continuablePieces = useMemo(
    () =>
      customerPieces.filter((p) => p.status === 'in-progress' || p.status === 'paused-will-return'),
    [customerPieces],
  );
  const customerBookings = useMemo(
    () => (customer?.bookings ?? []).map((id) => bookings[id]).filter(Boolean) as Booking[],
    [customer, bookings],
  );
  const referencePool = useMemo(
    () =>
      inspirationFor(customer).map((p) => ({
        url: p.url,
        caption: p.caption,
        capturedAt: p.capturedAt,
      })),
    [customer],
  );
  const references = useMemo(
    () =>
      partitionReferences({
        photos: referencePool,
        bookings: customerBookings,
        // Continuing a piece? Photos reused across that piece stay in the default view.
        currentPieceId: pieceMode === 'continue' ? continuePieceId || undefined : undefined,
        selectedUrls: selectedRefs,
        labelForBooking: (b) => simDateShort(b.start),
      }),
    [referencePool, customerBookings, pieceMode, continuePieceId, selectedRefs],
  );

  const continuePiece =
    pieceMode === 'continue' && continuePieceId ? pieces[continuePieceId] : undefined;
  const usingPiecePlan = piecePlanRate(continuePiece) != null;

  const standardDayRateFor = (session: SessionLength, aid: string, piece: Piece | undefined) => {
    const plan = piecePlanRate(piece);
    if (plan != null) return plan;
    const a = artists[aid];
    return a ? priceForArtist(a, session) : 0;
  };
  const standardDayRate = standardDayRateFor(sessionLength, primaryArtistId, continuePiece);
  const standardDeposit = depositForSession(sessionLength);

  // Prefill the structured fields from the customer's stored design intent so the
  // form looks populated for a known customer; reset piece + rate state too.
  useEffect(() => {
    const di = customer?.designIntent;
    setPieceMode('none');
    setContinuePieceId('');
    setPieceDefaults(null);
    setPrimaryArtistId(artistId);
    setCollabArtistIds([]);
    setStyleTags(di?.type && STYLE_TAG_OPTIONS.includes(di.type) ? [di.type] : []);
    setColour(di && /colou?r/.test(di.type) ? 'colour' : 'black-and-grey');
    setPlacement({ placementNote: di?.placement ?? '' });
    setSelectedRefs([]);
    setNotes('');
    const a = artists[artistId];
    setDayRateInput(a ? String(priceForArtist(a, sessionLength)) : '');
    setDepositInput(String(depositForSession(sessionLength)));
    setDayRateBasis('standard');
    setCustomHours(null);
    setOverrideActive(false);
    setShowRateReset(false);
    setOverrideReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    if (!depositMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (depositMenuRef.current && !depositMenuRef.current.contains(e.target as Node)) {
        setDepositMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [depositMenuOpen]);

  const searchResults = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const list = Object.values(customers);
    const ranked = q
      ? list.filter((c) =>
          `${c.firstName} ${c.lastName} ${c.phone} ${c.suburb}`.toLowerCase().includes(q),
        )
      : list.filter((c) => c.hero);
    return ranked.slice(0, 12);
  }, [customers, customerQuery]);

  // --- Rate synchronisation --------------------------------------------------
  const syncStandard = (session: SessionLength, aid: string, piece: Piece | undefined) => {
    setDayRateInput(String(standardDayRateFor(session, aid, piece)));
    setDepositInput(String(depositForSession(session)));
    setDayRateBasis('standard');
    setCustomHours(null);
    setOverrideActive(false);
    setShowRateReset(false);
  };

  // Picking a session card is the primary control — it always (re)sets the rate
  // + deposit from the rate card / piece plan.
  const changeSession = (s: SessionLength) => {
    setSessionLength(s);
    syncStandard(s, primaryArtistId, continuePiece);
  };

  const changePrimaryArtist = (id: string) => {
    setPrimaryArtistId(id);
    if (overrideActive) setShowRateReset(true);
    else syncStandard(sessionLength, id, continuePiece);
  };

  const chooseMode = (mode: PieceMode) => {
    setPieceMode(mode);
    if (mode !== 'new') {
      setNewPieceSessions('');
      setNewPieceTitle('');
    }
    if (mode !== 'continue') {
      setContinuePieceId('');
      setPieceDefaults(null);
      syncStandard(sessionLength, primaryArtistId, undefined);
    }
  };

  const selectContinuePiece = (pieceId: string) => {
    if (!pieceId) {
      setContinuePieceId('');
      setPieceDefaults(null);
      syncStandard(sessionLength, primaryArtistId, undefined);
      return;
    }
    const p = pieces[pieceId];
    if (!p) return;
    setContinuePieceId(pieceId);
    const nextColour: 'colour' | 'black-and-grey' = /colou?r/.test(p.designType)
      ? 'colour'
      : 'black-and-grey';
    const nextTags = STYLE_TAG_OPTIONS.includes(p.designType) ? [p.designType] : [];
    setPrimaryArtistId(p.assignedArtistId);
    setColour(nextColour);
    setStyleTags(nextTags);
    setPlacement({ placementNote: p.placement ?? '' });
    setSelectedRefs(p.referencePhotos ?? []);
    setPieceDefaults({
      placementNote: p.placement ?? '',
      colour: nextColour,
      styleTags: nextTags,
      artistId: p.assignedArtistId,
    });
    syncStandard(sessionLength, p.assignedArtistId, p);
  };

  const markOverride = () => {
    setOverrideActive(true);
    setShowRateReset(false);
  };
  const onDayRateChange = (v: string) => {
    setDayRateInput(v);
    markOverride();
  };
  const changeCustomHours = (h: number) => {
    const hours = Math.min(10, Math.max(0.5, h));
    setCustomHours(hours);
    const hourly = activeArtist?.rates.hourlyAud ?? 0;
    setDayRateInput(String(Math.round(hours * hourly)));
    setDepositInput(String(depositForDuration(hours * 60)));
    markOverride();
  };
  const onDayRateBasisChange = (v: DayRateBasis) => {
    setDayRateBasis(v);
    markOverride();
  };
  const onDepositChange = (v: string) => {
    setDepositInput(v);
    markOverride();
  };

  const toggleTag = (tag: string) =>
    setStyleTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const overridingPieceDefaults =
    pieceMode === 'continue' &&
    pieceDefaults != null &&
    (placement.placementNote !== pieceDefaults.placementNote ||
      colour !== pieceDefaults.colour ||
      primaryArtistId !== pieceDefaults.artistId ||
      styleTags.join('|') !== pieceDefaults.styleTags.join('|'));

  // --- Derived summary + money ----------------------------------------------
  const placementLabel = formatBodyPart({
    bodyPart: placement.bodyPart,
    bodyPartSide: placement.bodyPartSide,
    bodyPartQualifier: placement.bodyPartQualifier,
    placement: placement.placementNote,
  });
  const colourLabel = colour === 'colour' ? 'Colour' : 'Black & grey';
  const aiSummary = [placementLabel, colourLabel, styleTags.join(', ')].filter(Boolean).join(' · ');

  const depositAud =
    depositInput === '' ? standardDeposit : Math.max(0, Math.round(Number(depositInput) || 0));
  const canSave = !!customer && !!activeArtist;
  const canTakeDeposit = canSave && depositAud > 0;
  // No deposit (below the studio minimum) is a controlled path: privileged
  // personas waive directly (note-forced), everyone else raises an approval
  // request. Both replace the normal Save/Take-deposit actions.
  const needsApproval = requiresNoDepositApproval(depositAud);
  const isPrivileged = persona ? canApproveNoDepositOverride(persona) : false;
  const canSubmitNoDeposit = canSave && overrideReason.trim().length > 0;

  const effectiveDurationMin =
    customHours != null ? Math.round(customHours * 60) : SESSION_DURATION_MIN[sessionLength];
  const effectiveSessionLength =
    customHours != null ? sessionLengthForDuration(effectiveDurationMin) : sessionLength;

  const briefBookingFields = (): Partial<Booking> => ({
    placement: placement.placementNote.trim() || undefined,
    bodyPart: placement.bodyPart,
    bodyPartSide: placement.bodyPartSide,
    bodyPartQualifier: placement.bodyPartQualifier,
    colour,
    styleTags,
    dayRateAud:
      dayRateInput === '' ? standardDayRate : Math.max(0, Math.round(Number(dayRateInput) || 0)),
    dayRateBasis: dayRateBasis !== 'standard' ? dayRateBasis : undefined,
    collabArtistIds: collabArtistIds.length ? collabArtistIds : undefined,
    aiSummary: aiSummary || undefined,
  });

  const deriveBrief = () =>
    aiSummary ||
    notes ||
    `${customer?.designIntent?.type ?? 'consult'} · ${customer?.designIntent?.placement ?? ''}`;

  // Links the just-created booking to a piece (continue → append; new → create),
  // and returns the pieceId to stamp on the booking. Reads live store state so
  // the piece's bookingsLinked isn't clobbered by a stale snapshot.
  const resolvePieceForBooking = (bookingId: string): string | undefined => {
    const brain = useBrainStore.getState();
    if (pieceMode === 'continue' && continuePieceId) {
      const p = brain.pieces[continuePieceId];
      if (p && !p.bookingsLinked.includes(bookingId)) {
        brain.upsertPiece({ ...p, bookingsLinked: [...p.bookingsLinked, bookingId] });
      }
      return continuePieceId;
    }
    if (pieceMode === 'new' && customer) {
      const id = `piece-${bookingId}`;
      const sessions = Math.round(Number(newPieceSessions) || 0) > 0
        ? Math.round(Number(newPieceSessions))
        : undefined;
      const designType: DesignType =
        (styleTags[0] as DesignType | undefined) ??
        (colour === 'colour' ? 'colour-realism' : 'black-and-grey');
      brain.upsertPiece({
        id,
        customerId: customer.id,
        title: newPieceTitle.trim() || undefined,
        designType,
        placement: placement.placementNote.trim() || (customer.designIntent?.placement ?? ''),
        style: styleTags[0] ?? designType,
        referencePhotos: selectedRefs,
        assignedArtistId: primaryArtistId,
        estimatedTotalHours: sessions ? Math.round(sessions * (effectiveDurationMin / 60) * 2) / 2 : 0,
        hoursCompleted: 0,
        estimatedTotalSessions: sessions,
        sessionsCompleted: 0,
        status: 'in-progress',
        bookingsLinked: [bookingId],
      });
      // Write the reverse link too. Nothing else did, which is why a piece
      // created here never showed up as continuable on the next visit.
      if (!customer.pieces.includes(id)) {
        brain.upsertCustomer({ ...customer, pieces: [...customer.pieces, id] });
      }
      return id;
    }
    return undefined;
  };

  const baseBooking = (id: string, status: Booking['status'], pieceId?: string): Booking => ({
    id,
    customerId: customer!.id,
    studioId: studioId as Booking['studioId'],
    artistId: primaryArtistId,
    pieceId,
    start: startIso,
    durationMin: effectiveDurationMin,
    sessionLength: effectiveSessionLength,
    status,
    designBrief: deriveBrief(),
    referencePhotos: selectedRefs,
    source: 'agent-initiated-chat',
    createdAt: clock,
    notes,
    ...briefBookingFields(),
  });

  const saveAsHeld = () => {
    if (!customer || !activeArtist) return;
    const id = `bk-${Date.now()}`;
    const pieceId = resolvePieceForBooking(id);
    const booking = baseBooking(id, 'held', pieceId);
    upsertBooking(booking);
    appendActivity({
      id: `act-bk-${id}`,
      type: 'booking-created',
      customerId: customer.id,
      studioId: booking.studioId,
      artistId: activeArtist.id,
      body: `Held ${simTimeFull(startIso)} for ${customerName(customer)} with ${activeArtist.displayName}.`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'info',
      title: 'Booking held',
      body: 'Auto-expires in 24h if no deposit lands.',
      ttlMs: 4500,
    });
    onClose();
  };

  const sendDepositLink = () => {
    if (!customer || !activeArtist || depositAud <= 0) return;
    setDepositMenuOpen(false);
    const dep = generateDepositLink({
      customerId: customer.id,
      studioId: studioId as Booking['studioId'],
      artistId: primaryArtistId,
      amountAud: depositAud,
      channel: 'sms',
      sessionLength: effectiveSessionLength,
      source: 'agent-initiated-chat',
    });
    const bkId = `bk-${dep.id}`;
    const pieceId = resolvePieceForBooking(bkId);
    setTimeout(
      () =>
        simulateStripeWebhookPaid({
          depositId: dep.id,
          bookingStartIso: startIso,
          designBrief: deriveBrief(),
          referencePhotos: selectedRefs,
          bookingOverrides: {
            ...briefBookingFields(),
            pieceId,
            artistId: primaryArtistId,
            sessionLength: effectiveSessionLength,
            durationMin: effectiveDurationMin,
            notes: notes || undefined,
          },
        }),
      1800,
    );
    pushNotification({
      intensity: 'high',
      title: 'Deposit link sent',
      body: `${aud(depositAud)} link queued. Webhook simulates in ~2s.`,
      ttlMs: 5000,
    });
    onClose();
  };

  // Cash / EFTPOS / bank transfer: deposit is paid on the spot, so the booking is
  // placed immediately as confirmed with a paid deposit — no reminder cadence.
  const takeManual = (method: 'cash' | 'eftpos' | 'bank-transfer') => {
    if (!customer || !activeArtist || depositAud <= 0) return;
    setDepositMenuOpen(false);
    const id = `bk-${Date.now()}`;
    const pieceId = resolvePieceForBooking(id);
    const booking = baseBooking(id, 'confirmed', pieceId);
    upsertBooking(booking);
    appendActivity({
      id: `act-bk-${id}`,
      type: 'booking-created',
      customerId: customer.id,
      studioId: booking.studioId,
      artistId: activeArtist.id,
      body: `Confirmed ${simTimeFull(startIso)} for ${customerName(customer)} with ${activeArtist.displayName}.`,
      timestamp: clock,
    });
    recordManualPayment({
      bookingId: id,
      amountAud: depositAud,
      method,
      by: persona ?? 'front-desk',
    });
    // Walk-in booking is confirmed on the spot — auto-send its waiver.
    onBookingConfirmed(id);
    const methodLabel =
      method === 'bank-transfer' ? 'bank transfer' : method === 'eftpos' ? 'EFTPOS' : 'cash';
    pushNotification({
      intensity: 'high',
      title: 'Deposit paid',
      body: `${aud(depositAud)} by ${methodLabel} · booking confirmed. No reminder cadence.`,
      ttlMs: 4500,
      customerId: customer.id,
    });
    onClose();
  };

  // Creates the held booking that both no-deposit paths hang off. Kept separate
  // so request + direct-waive share one booking-creation site.
  const createHeldNoDepositBooking = (): string | undefined => {
    if (!customer || !activeArtist) return undefined;
    const id = `bk-${Date.now()}`;
    const pieceId = resolvePieceForBooking(id);
    upsertBooking(baseBooking(id, 'held', pieceId));
    appendActivity({
      id: `act-bk-${id}`,
      type: 'booking-created',
      customerId: customer.id,
      studioId: studioId as Booking['studioId'],
      artistId: activeArtist.id,
      body: `Held ${simTimeFull(startIso)} for ${customerName(customer)} with ${activeArtist.displayName} — no deposit.`,
      timestamp: clock,
    });
    return id;
  };

  // Non-privileged: raise a no-deposit approval request. Booking stays held,
  // badged "awaiting no-deposit approval" until Georgina resolves it.
  const requestNoDeposit = () => {
    if (!canSubmitNoDeposit) return;
    const id = createHeldNoDepositBooking();
    if (!id) return;
    requestNoDepositOverride({
      bookingId: id,
      requestedBy: persona ?? 'front-desk',
      note: overrideReason,
    });
    pushNotification({
      intensity: 'high',
      title: 'Sent for approval',
      body: 'Slot held. Georgina will approve or reject the no-deposit request.',
      ttlMs: 5000,
      customerId: customer!.id,
    });
    onClose();
  };

  // Privileged (Georgina / owner): waive the deposit directly. Booking confirms
  // immediately with the waiver auto-sent; still note-forced + logged.
  const waiveNoDeposit = () => {
    if (!canSubmitNoDeposit) return;
    const id = createHeldNoDepositBooking();
    if (!id) return;
    waiveDepositDirectly({ bookingId: id, by: persona ?? 'front-desk', note: overrideReason });
    pushNotification({
      intensity: 'high',
      title: 'Booking confirmed — no deposit',
      body: 'Deposit waived and logged. Customer pays in full on the day.',
      ttlMs: 4500,
      customerId: customer!.id,
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 8, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-3xl p-6 md:p-7 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="new-booking-modal"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 text-cream-soft hover:text-cream"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <p className="label-small-caps text-gold-bright">New booking</p>
            <h2 className="heading-display text-2xl text-cream mt-2">
              {activeArtist?.displayName ? `${activeArtist.displayName} · ` : ''}
              {simTimeFull(startIso)}
            </h2>

            {/* Two-column body echoing the booking detail card: left = who the
                booking is for and who's working it; right = the design brief.
                Session & rate + notes span full width below. */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-6 gap-y-4">
              {/* Left column — customer, piece continuity, artists */}
              <div className="min-w-0 space-y-4" data-testid="new-booking-col-left">
                {/* Customer */}
                <div className="block" ref={customerInputRef}>
                  <span className="label-small-caps">Customer</span>
                  {customer && !customerOpen ? (
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerOpen(true);
                        setCustomerQuery('');
                      }}
                      className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-left flex items-center justify-between hover:border-gold-bright/60 transition"
                      data-testid="new-booking-customer-selected"
                    >
                      <span>
                        <span className="font-inter text-sm text-cream">
                          {customerName(customer)}
                        </span>
                        <span className="font-mono text-[10px] text-cream-soft uppercase tracking-widest ml-2">
                          {customer.phone}
                        </span>
                      </span>
                      <span className="font-mono text-[10px] text-cream-faint uppercase tracking-widest">
                        Change
                      </span>
                    </button>
                  ) : (
                    <div className="relative mt-2">
                      <Search
                        size={12}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-cream-faint"
                      />
                      <input
                        autoFocus
                        type="text"
                        value={customerQuery}
                        onChange={(e) => setCustomerQuery(e.target.value)}
                        placeholder="Search name, phone, or suburb"
                        className="w-full bg-ink-black border border-gold-dim/40 rounded-md pl-8 pr-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                        data-testid="new-booking-customer-search"
                      />
                      <ul
                        className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto panel-dark py-1 shadow-ink-soft"
                        data-testid="new-booking-customer-results"
                      >
                        {searchResults.length === 0 ? (
                          <li className="px-3 py-2 font-serif text-sm text-cream-soft">
                            No matches.
                          </li>
                        ) : null}
                        {searchResults.map((c) => {
                          const active = c.id === customerId;
                          return (
                            <li key={c.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  setCustomerId(c.id);
                                  setCustomerOpen(false);
                                }}
                                className={clsx(
                                  'w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-ink-soft/60 transition',
                                  active ? 'text-gold-bright' : 'text-cream',
                                )}
                                data-testid={`new-booking-customer-option-${c.id}`}
                              >
                                <span className="flex flex-col">
                                  <span className="font-inter text-sm">{customerName(c)}</span>
                                  <span className="font-mono text-[10px] text-cream-soft uppercase tracking-widest">
                                    {c.phone} · {c.suburb}
                                  </span>
                                </span>
                                {active ? <Check size={12} /> : null}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Piece */}
                <div className="block">
                  <span className="label-small-caps">Piece</span>
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="new-booking-piece-mode">
                    {PIECE_MODES.map((m) => {
                      const disabled = m.key === 'continue' && continuablePieces.length === 0;
                      return (
                        <button
                          key={m.key}
                          type="button"
                          disabled={disabled}
                          onClick={() => chooseMode(m.key)}
                          className={clsx(
                            'rounded-md border px-2.5 py-2 text-[11px] text-left transition',
                            pieceMode === m.key
                              ? 'border-gold-bright bg-gold/10 text-gold-bright'
                              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                            disabled && 'opacity-40 cursor-not-allowed hover:border-gold-dim/30',
                          )}
                          data-testid={`new-booking-piece-mode-${m.key}`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>

                  {pieceMode === 'continue' ? (
                    <div className="mt-2">
                      <select
                        value={continuePieceId}
                        onChange={(e) => selectContinuePiece(e.target.value)}
                        className="w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                        data-testid="new-booking-piece-select"
                      >
                        <option value="">Choose a piece…</option>
                        {continuablePieces.map((p) => (
                          <option key={p.id} value={p.id}>
                            {(p.title || p.style || p.designType.replace(/-/g, ' ')) +
                              ` · session ${pieceSessionNo(p)}${p.estimatedTotalSessions ? ` of ${p.estimatedTotalSessions}` : ''}`}
                          </option>
                        ))}
                      </select>
                      {overridingPieceDefaults ? (
                        <p
                          className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-300"
                          data-testid="new-booking-piece-override"
                        >
                          <AlertTriangle size={11} /> Overriding piece defaults for this booking.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {pieceMode === 'new' ? (
                    <>
                    <label className="mt-2 block">
                      <span className="label-small-caps">Piece name</span>
                      <input
                        type="text"
                        value={newPieceTitle}
                        onChange={(e) => setNewPieceTitle(e.target.value)}
                        placeholder="e.g. Dragon sleeve"
                        className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                        data-testid="new-booking-piece-title"
                      />
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                        Optional — how this piece is referred to everywhere (falls back to style).
                      </p>
                    </label>
                    <label className="mt-2 block">
                      <span className="label-small-caps">Estimated sessions</span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={newPieceSessions}
                        onChange={(e) => setNewPieceSessions(e.target.value)}
                        placeholder="e.g. 4"
                        className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                        data-testid="new-booking-piece-sessions"
                      />
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                        Optional — drives the &quot;session X of Y&quot; continuity on the piece.
                      </p>
                    </label>
                    </>
                  ) : null}
                </div>

                {/* Artist */}
                <div className="block">
                  <span className="label-small-caps">
                    Artist <span className="text-blood">*</span>
                  </span>
                  <div className="mt-2">
                    <ArtistPicker
                      artists={studioArtists}
                      value={primaryArtistId}
                      onSelect={changePrimaryArtist}
                      placeholder="Select the artist doing this piece"
                      testid="new-booking-primary-artist"
                      required
                    />
                  </div>
                </div>

                {/* Collab */}
                <div className="block">
                  <span className="label-small-caps">Collab with</span>
                  <div
                    className="mt-2 flex flex-wrap items-center gap-1.5"
                    data-testid="new-booking-collab"
                  >
                    {collabArtistIds.map((id) => {
                      const a = artists[id];
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-full border border-gold-bright bg-gold/15 px-2.5 py-1 text-[11px] text-gold-bright"
                          data-testid={`new-booking-collab-chip-${id}`}
                        >
                          {a?.displayName ?? id}
                          <button
                            type="button"
                            onClick={() =>
                              setCollabArtistIds((prev) => prev.filter((x) => x !== id))
                            }
                            aria-label={`Remove ${a?.displayName ?? id}`}
                            className="hover:text-cream"
                          >
                            <X size={11} />
                          </button>
                        </span>
                      );
                    })}
                    <div className="min-w-[190px]">
                      <ArtistPicker
                        artists={studioArtists}
                        excludeIds={[primaryArtistId, ...collabArtistIds]}
                        onSelect={(id) =>
                          setCollabArtistIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
                        }
                        triggerLabel="+ Add collab artist"
                        testid="new-booking-collab-add"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* end left column */}

              {/* Right column — the design brief: references, placement, colour, style */}
              <div className="min-w-0 space-y-4" data-testid="new-booking-col-right">
                {/* References */}
                {references.photos.length > 0 ? (
                  <div data-testid="new-booking-customer-inspiration">
                    <ReferencePicker
                      photos={references.photos}
                      hiddenCount={references.hiddenCount}
                      selected={selectedRefs}
                      onChange={setSelectedRefs}
                      label="References — tap what they sent to attach"
                      testid="new-booking-references"
                    />
                  </div>
                ) : null}

                {/* Placement */}
                <div>
                  <PlacementFields
                    value={placement}
                    onChange={(patch) => setPlacement((p) => ({ ...p, ...patch }))}
                    idPrefix="new-booking"
                  />
                </div>

                {/* Colour */}
                <div className="block">
                  <span className="label-small-caps">Colour</span>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {(['black-and-grey', 'colour'] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setColour(c)}
                        className={
                          colour === c
                            ? 'rounded-md border border-gold-bright bg-gold/10 px-3 py-2 text-xs text-gold-bright capitalize'
                            : 'rounded-md border border-gold-dim/30 px-3 py-2 text-xs text-cream-soft hover:border-gold-bright/60 capitalize'
                        }
                        data-testid={`new-booking-colour-${c}`}
                      >
                        {c === 'colour' ? 'Colour' : 'Black & grey'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Style tags */}
                <div className="block">
                  <span className="label-small-caps">Style tags</span>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="new-booking-style-tags">
                    {STYLE_TAG_OPTIONS.map((tag) => {
                      const active = styleTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(tag)}
                          className={clsx(
                            'rounded-full border px-2.5 py-1 text-[11px] capitalize transition',
                            active
                              ? 'border-gold-bright bg-gold/15 text-gold-bright'
                              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                          )}
                          data-testid={`new-booking-tag-${tag}`}
                        >
                          {tag.replace(/-/g, ' ')}
                        </button>
                      );
                    })}
                  </div>
                  {aiSummary ? (
                    <p
                      className="mt-2 font-serif text-[12px] text-cream-soft"
                      data-testid="new-booking-ai-summary"
                    >
                      <span className="label-small-caps text-gold-bright mr-1.5">Summary</span>
                      {aiSummary}
                    </p>
                  ) : null}
                </div>
              </div>
              {/* end right column */}
            </div>
            {/* end two-column body */}

            {/* Session length + rate + deposit — full width below both columns */}
            <div className="block mt-6">
              <span className="label-small-caps">Session &amp; rate</span>
              <div className="mt-2">
                <RateCardBlock
                  artist={activeArtist}
                  sessionLength={sessionLength}
                  onSessionChange={changeSession}
                  dayRate={dayRateInput}
                  onDayRateChange={onDayRateChange}
                  customHours={customHours}
                  onCustomHoursChange={changeCustomHours}
                  dayRateBasis={dayRateBasis}
                  onDayRateBasisChange={onDayRateBasisChange}
                  deposit={depositInput}
                  onDepositChange={onDepositChange}
                  standardDayRate={standardDayRate}
                  standardDeposit={standardDeposit}
                  usingPiecePlan={usingPiecePlan}
                  showRateReset={showRateReset}
                  onResetToStandard={() =>
                    syncStandard(sessionLength, primaryArtistId, continuePiece)
                  }
                  idPrefix="new-booking"
                />
              </div>
            </div>

            {/* Notes */}
            <label className="block mt-4">
              <span className="label-small-caps">Notes</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Session-specific detail only (allergy specifics, cover-up context, temporary marks, tricky positioning). Do not enter deposit, rebook, day rate, or design info — those have fields above."
                className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
              />
            </label>

            {!activeArtist ? (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-blood/80">
                Select an artist to save or take a deposit.
              </p>
            ) : null}

            {/* No-deposit path — a $0 deposit can't be booked directly by a
                non-privileged persona. Reason is mandatory either way. */}
            {needsApproval && canSave ? (
              <div
                className="mt-5 rounded-md border border-amber-400/40 bg-amber-400/5 p-3"
                data-testid="no-deposit-block"
              >
                <p className="flex items-start gap-1.5 text-[12px] text-amber-300">
                  <AlertTriangle size={13} className="mt-px shrink-0" />
                  {isPrivileged
                    ? 'No deposit on this booking. Waiving it needs a reason — it will be logged against the booking.'
                    : "No deposit on this booking. It can't be booked directly — send it to Georgina for no-deposit approval."}
                </p>
                <label className="mt-2 block">
                  <span className="label-small-caps">
                    No-deposit reason <span className="text-blood">*</span>
                  </span>
                  <textarea
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    rows={2}
                    placeholder="e.g. trusted repeat customer, paying full on the day"
                    className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                    data-testid="no-deposit-reason"
                  />
                </label>
              </div>
            ) : null}

            {/* Actions */}
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={onClose} className="btn-ghost text-xs">
                Cancel
              </button>
              {needsApproval ? (
                isPrivileged ? (
                  <button
                    type="button"
                    onClick={waiveNoDeposit}
                    disabled={!canSubmitNoDeposit}
                    className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="waive-no-deposit"
                    title={
                      canSubmitNoDeposit
                        ? 'Confirm with no deposit'
                        : 'Add a reason to waive the deposit'
                    }
                  >
                    Confirm — no deposit
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={requestNoDeposit}
                    disabled={!canSubmitNoDeposit}
                    className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="request-no-deposit"
                    title={
                      canSubmitNoDeposit
                        ? 'Send for no-deposit approval'
                        : 'Add a reason to request approval'
                    }
                  >
                    Request no-deposit approval
                  </button>
                )
              ) : (
                <>
                  <button
                    type="button"
                    onClick={saveAsHeld}
                    disabled={!canSave}
                    className="btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="new-booking-save-held"
                  >
                    <Save size={12} /> Save as held
                  </button>
                  <div className="relative" ref={depositMenuRef}>
                <button
                  type="button"
                  onClick={() => setDepositMenuOpen((v) => !v)}
                  disabled={!canTakeDeposit}
                  className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="take-deposit"
                  aria-haspopup="menu"
                  aria-expanded={depositMenuOpen}
                  title={
                    canTakeDeposit
                      ? 'Collect the deposit'
                      : 'Select a customer, artist and deposit above $0'
                  }
                >
                  Take deposit {aud(depositAud)} <ChevronDown size={12} />
                </button>
                {depositMenuOpen ? (
                  <div
                    className="absolute bottom-full right-0 mb-1 w-60 panel-dark py-1 shadow-ink-soft"
                    data-testid="take-deposit-menu"
                    role="menu"
                  >
                    <button
                      type="button"
                      onClick={sendDepositLink}
                      className="w-full text-left px-3 py-2 text-sm text-cream hover:bg-ink-soft/60 transition flex items-center gap-2"
                      data-testid="send-deposit-now"
                    >
                      <Send size={13} className="text-gold-bright shrink-0" /> Send Ink Book /
                      Stripe link
                    </button>
                    <div className="my-1 border-t border-gold-dim/20" />
                    {(
                      [
                        { m: 'cash', label: 'Paid by cash' },
                        { m: 'eftpos', label: 'Paid by EFTPOS' },
                        { m: 'bank-transfer', label: 'Paid by bank transfer' },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.m}
                        type="button"
                        onClick={() => takeManual(opt.m)}
                        className="w-full text-left px-3 py-2 text-sm text-cream hover:bg-ink-soft/60 transition flex items-center gap-2"
                        data-testid={`take-deposit-${opt.m}`}
                      >
                        <Banknote size={13} className="text-mint shrink-0" /> {opt.label}
                      </button>
                    ))}
                  </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
